import { useState, type FormEvent } from "react";
import PublicTermsLayout from "../terms/PublicTermsLayout";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8081";

const AccountDeletionPage = () => {
  const [appType, setAppType] = useState("KEEPER");
  const [requestType, setRequestType] = useState("ACCOUNT");
  const [loginProvider, setLoginProvider] = useState("EMAIL");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    if (!contactEmail.trim() && !contactPhone.trim()) {
      setMessage("가입 시 등록한 이메일 또는 전화번호 중 하나를 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/public/account-deletion-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appType, requestType, loginProvider, contactEmail: contactEmail || null, contactPhone: contactPhone || null, requestReason: requestReason || null }),
      });
      const payload = await response.json() as { success?: boolean };
      if (!response.ok || payload.success !== true) throw new Error("request failed");
      setMessage("요청이 접수되었습니다. 계정 확인 후 등록된 연락처를 통해 처리 절차를 안내합니다.");
      setContactEmail(""); setContactPhone(""); setRequestReason("");
    } catch {
      setMessage("요청을 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally { setSubmitting(false); }
  };

  const fieldClass = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
  return (
    <PublicTermsLayout>
      <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <h1 className="text-2xl font-bold">다을 계정 및 데이터 삭제 요청</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">다을 또는 다을 보호자 앱을 이용할 수 없는 경우 이 페이지에서 삭제 요청을 시작할 수 있습니다. 비밀번호, 인증번호, 소셜 로그인 토큰은 입력하지 마세요.</p>

        <section className="mt-7 rounded-xl bg-blue-50 p-4 text-sm leading-6 text-blue-950">
          <h2 className="font-bold">앱에서 직접 탈퇴하는 방법</h2>
          <p>다을: MY/설정 → 계정 설정 → 회원 탈퇴</p>
          <p>다을 보호자: MY → 계정 및 보안 → 회원 탈퇴</p>
        </section>

        <form className="mt-7 grid gap-5" onSubmit={submit}>
          <label className="text-sm font-semibold">이용 앱<select className={fieldClass} value={appType} onChange={(e) => setAppType(e.target.value)}><option value="SENIOR">다을</option><option value="KEEPER">다을 보호자</option></select></label>
          <label className="text-sm font-semibold">요청 범위<select className={fieldClass} value={requestType} onChange={(e) => setRequestType(e.target.value)}><option value="ACCOUNT">계정 및 관련 데이터 전체 삭제</option><option value="PARTIAL_DATA">계정은 유지하고 일부 데이터 삭제</option></select></label>
          <label className="text-sm font-semibold">가입 방식<select className={fieldClass} value={loginProvider} onChange={(e) => setLoginProvider(e.target.value)}><option value="EMAIL">이메일</option><option value="GOOGLE">Google</option><option value="KAKAO">Kakao</option><option value="APPLE">Apple</option><option value="OTHER">기타</option></select></label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-semibold">가입 시 등록한 이메일<input className={fieldClass} type="email" maxLength={255} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} autoComplete="email" /></label>
            <label className="text-sm font-semibold">가입 시 등록한 전화번호<input className={fieldClass} type="tel" maxLength={30} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} autoComplete="tel" /></label>
          </div>
          <label className="text-sm font-semibold">{requestType === "PARTIAL_DATA" ? "삭제할 데이터와 범위(필수)" : "요청 사유(선택)"}<textarea className={`${fieldClass} min-h-24`} required={requestType === "PARTIAL_DATA"} maxLength={500} value={requestReason} onChange={(e) => setRequestReason(e.target.value)} /></label>
          <button className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700 disabled:opacity-60" disabled={submitting}>{submitting ? "접수 중..." : "삭제 요청 접수"}</button>
          {message && <p role="status" className="rounded-xl bg-slate-100 p-4 text-sm">{message}</p>}
        </form>

        <section className="mt-8 border-t border-slate-200 pt-6 text-sm leading-6 text-slate-600">
          <h2 className="font-bold text-slate-900">삭제 및 보관 안내</h2>
          <p className="mt-2">본인 확인 후 계정, 프로필, 로그인 연결, 가족 연결 및 계정과 직접 연결된 운영 정보가 삭제됩니다. 법령상 보관이 필요한 위치정보 이용·제공 확인자료는 6개월, 소비자 분쟁처리 기록은 3년간 분리 보관 후 삭제될 수 있습니다.</p>
          <p className="mt-2">일부 데이터는 개인정보처리방침에 따라 별도 보관될 수 있습니다. 가명처리된 데이터를 익명 데이터로 간주하지 않으며, 구체적인 보관 대상과 기간은 개인정보처리방침을 확인해 주세요. 일부 데이터 삭제 요청은 계정을 유지한 채 요청 범위와 처리 가능 여부를 확인한 후 진행합니다.</p>
        </section>
      </article>
    </PublicTermsLayout>
  );
};

export default AccountDeletionPage;
